"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useState } from "react";

interface SelfieRejectedDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmReset: () => Promise<void>;
}

export function SelfieRejectedDialog({
  isOpen,
  onOpenChange,
  onConfirmReset,
}: SelfieRejectedDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirmReset();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <span>Verification Failed</span>
          </DialogTitle>
          <DialogDescription className="pt-2">
            Your last selfie verification was rejected by Instacart.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-3">
          <p className="text-sm text-muted-foreground">
            To continue using BatchFinder, you&apos;ll need to reset your Instacart connection and re-link your account.
          </p>
          <div className="p-3 bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-md text-sm">
            <p className="font-medium">What happens when you reset:</p>
            <ul className="list-disc list-inside mt-1 space-y-1 text-xs">
              <li>Your Instacart session will be disconnected</li>
              <li>You&apos;ll need to re-link your Instacart account</li>
              <li>A new verification selfie will be required</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting...
              </>
            ) : (
              "Reset & Re-link"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
